import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import InputMask from 'react-input-mask';

import {
  crmApi,
  duplicateIdentityConflict,
  duplicatePhoneWarning,
  errMsg,
} from '@crm/api';
import {
  normalizeRuMobilePhone,
  RU_MOBILE_FORMAT_CHARS,
  RU_MOBILE_MASK,
} from '@shared/utils/ruMobilePhone';
import { UiButton, UiCheckbox, UiInput, UiSelect } from '@shared/common/ui';

export function ClientsNewClientModal({
  open,
  onClose,
  onCreated,
  navigateOnCreate = true,
  /** Создание родственника — без статуса «Новый клиент» на филиале. */
  asRelative = false,
}) {
  const { ownerId, branchId } = useParams();
  const navigate = useNavigate();
  const [fio, setFio] = useState('');
  const [phone, setPhone] = useState('');
  const [gender, setGender] = useState('1');
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [selectedClientIds, setSelectedClientIds] = useState(() => new Set());
  const [attaching, setAttaching] = useState(false);
  const [attachError, setAttachError] = useState(null);

  useEffect(() => {
    if (open) {
      setFio('');
      setPhone('');
      setGender('1');
      setErr(null);
      setDuplicateWarning(null);
      setSelectedClientIds(new Set());
      setAttachError(null);
      setAttaching(false);
    }
  }, [open]);

  if (!open) return null;

  const busy = saving || attaching;
  const clients = duplicateWarning?.clients ?? [];
  const selectedCount = selectedClientIds.size;
  const canSubmitAttach = selectedCount > 0 && !busy;

  const finishWithClient = async (client) => {
    const id = client?.id;
    if (id && onCreated) {
      await onCreated(client);
    }
    onClose();
    if (id && navigateOnCreate) navigate(`/${ownerId}/crm/${branchId}/clients/${id}`);
  };

  const openDuplicateWarning = (warning) => {
    setDuplicateWarning(warning);
    setSelectedClientIds(new Set());
    setAttachError(null);
  };

  const closeDuplicateWarning = () => {
    if (busy) return;
    setDuplicateWarning(null);
    setSelectedClientIds(new Set());
    setAttachError(null);
  };

  const toggleClient = (clientId, checked) => {
    setSelectedClientIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(clientId);
      else next.delete(clientId);
      return next;
    });
  };

  const attachToCurrentBranch = async () => {
    if (!canSubmitAttach) return;
    const ids = [...selectedClientIds];
    setAttachError(null);
    setAttaching(true);
    try {
      for (const clientId of ids) {
        await crmApi(branchId).post(`/clients/${clientId}/attach-current-branch`);
      }
      const first =
        clients.find((item) => item.id === ids[0]) ??
        ({ id: ids[0], fio: fio.trim() || undefined });
      setDuplicateWarning(null);
      setSelectedClientIds(new Set());
      await finishWithClient({ id: first.id, fio: first.fio });
    } catch (ex) {
      setAttachError(errMsg(ex, 'Не удалось привязать клиента к филиалу'));
    } finally {
      setAttaching(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setDuplicateWarning(null);
    setSelectedClientIds(new Set());
    setAttachError(null);
    const f = fio.trim();
    const normalizedPhone = normalizeRuMobilePhone(phone);
    if (!f) {
      setErr('Укажите Ф.И.О.');
      return;
    }
    if (!normalizedPhone) {
      setErr('Укажите мобильный телефон в формате +7 (9xx) xxx-xx-xx');
      return;
    }
    setSaving(true);
    try {
      const { data } = await crmApi(branchId).post('/clients', {
        fio: f,
        phone: normalizedPhone,
        gender: Number(gender),
        ...(asRelative ? { asRelative: true } : {}),
      });
      await finishWithClient(data.client);
    } catch (ex) {
      const identity = duplicateIdentityConflict(ex);
      if (identity) {
        if (identity.canAttach) {
          openDuplicateWarning(identity);
          return;
        }
        setErr(identity.message);
        return;
      }
      const warning = duplicatePhoneWarning(ex);
      if (warning) {
        openDuplicateWarning(warning);
        return;
      }
      setErr(errMsg(ex, 'Не удалось создать клиента'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-client-title"
        onClick={(ev) => ev.stopPropagation()}
      >
        <header className="modal-card__header">
          <h2 id="new-client-title">Добавить клиента</h2>
          <UiButton variant="ghost" size="sm" type="button" onClick={onClose} aria-label="Закрыть">
            ×
          </UiButton>
        </header>
        <form onSubmit={submit} className="modal-card__body">
          <UiInput label="Ф.И.О." value={fio} onChange={(e) => setFio(e.target.value)} autoFocus />
          <label className="ui-field">
            <span className="ui-field__label">Телефон</span>
            <InputMask
              className="ui-control"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              mask={RU_MOBILE_MASK}
              formatChars={RU_MOBILE_FORMAT_CHARS}
              maskChar={null}
              placeholder="+7 (9xx) xxx-xx-xx"
            />
          </label>
          <UiSelect
            label="Пол"
            value={gender}
            onChange={(value) => setGender(String(value))}
            options={[
              { value: '1', label: 'Мужской' },
              { value: '2', label: 'Женский' },
            ]}
          />
          {err && <div className="form-error">{err}</div>}
          <div className="modal-card__actions">
            <UiButton type="submit" disabled={busy}>
              {saving ? 'Создание…' : 'Добавить'}
            </UiButton>
            <UiButton variant="ghost" type="button" onClick={onClose}>
              Отменить
            </UiButton>
          </div>
        </form>
      </div>

      {duplicateWarning ? (
        <div
          className="modal-backdrop"
          role="presentation"
          style={{ zIndex: 30 }}
          onClick={closeDuplicateWarning}
        >
          <div
            className="modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="duplicate-client-title"
            onClick={(ev) => ev.stopPropagation()}
          >
            <header className="modal-card__header">
              <h2 id="duplicate-client-title">Клиент уже существует</h2>
              <UiButton
                variant="ghost"
                size="sm"
                type="button"
                disabled={busy}
                onClick={closeDuplicateWarning}
                aria-label="Закрыть"
              >
                ×
              </UiButton>
            </header>
            <div className="modal-card__body">
              <p style={{ marginTop: 0 }}>{duplicateWarning.message}</p>
              {clients.length > 0 ? (
                <div
                  role="group"
                  aria-label="Найденные клиенты"
                  style={{ display: 'grid', gap: 8, marginBottom: 12 }}
                >
                  {clients.map((client) => {
                    const checked = selectedClientIds.has(client.id);
                    const disabled = client.inCurrentBranch || busy;
                    const branchHint =
                      client.branches.length > 0
                        ? client.branches.join(', ')
                        : 'без филиала';
                    return (
                      <UiCheckbox
                        key={client.id}
                        label={`${client.fio}${
                          client.inCurrentBranch
                            ? ' (уже в текущем филиале)'
                            : ` — ${branchHint}`
                        }`}
                        checked={checked}
                        disabled={disabled}
                        onChange={(e) => toggleClient(client.id, e.target.checked)}
                      />
                    );
                  })}
                </div>
              ) : null}
              {attachError ? <div className="form-error">{attachError}</div> : null}
              <div className="modal-card__actions">
                <UiButton
                  type="button"
                  disabled={!canSubmitAttach}
                  onClick={attachToCurrentBranch}
                >
                  {attaching ? 'Привязка…' : 'Привязать к текущему филиалу'}
                </UiButton>
                <UiButton
                  variant="ghost"
                  type="button"
                  disabled={busy}
                  onClick={closeDuplicateWarning}
                >
                  Отменить
                </UiButton>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
